import { Divider, Icon, ICONS, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { Accordion } from 'semantic-ui-react';
import { EM_DASH } from '../../../../Base.constants';
import { AccordionItem, AccordionWidgetData } from '../../../../models/RiskProducts';
import styles from './ExpandableMetric.module.scss';

const cx = classNames.bind(styles);

interface ExpandableTitleProps {
  metric: AccordionWidgetData;
  isItemExpanded: boolean;
  isTitleValueShown?: boolean;
  print?: boolean;
}

const ExpandableMetric = ({
  metric,
  isItemExpanded,
  isTitleValueShown = true,
  print,
}: ExpandableTitleProps): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isItemExpanded);
  const handleTitleClick = (): void => {
    setIsExpanded(!isExpanded);
  };

  const formatTitleValue = (value) => {
    const emptyText = isTitleValueShown ? EM_DASH : '';
    return value || emptyText;
  };

  const formatLineValue = (value) => {
    return value || EM_DASH;
  };

  const renderIcon = (expanded: boolean) => {
    const label = expanded ? 'Collapse' : 'Expand';
    const caretClass = cx('caret', {
      expanded,
    });

    return (
      <Tooltip
        key={label}
        trigger={
          <span className={caretClass}>
            <Icon width={12} height={12} name={ICONS.CaretDown} dataTestId="expand-icon" />
          </span>
        }
        content={label}
      />
    );
  };

  const renderOverviewContent = (items: AccordionItem[]) => {
    return items.map((item) => {
      return renderOverviewContentLine(item);
    });
  };

  const renderOverviewContentLine = (item: AccordionItem, isTitle: boolean = false) => {
    return (
      <tr key={`overview-content-${item.label}`}>
        <td>{item.label}</td>
        {isTitle ? <td>{formatTitleValue(item.value)}</td> : <td>{formatLineValue(item.value)}</td>}
      </tr>
    );
  };

  const isExpandable = (metric) => {
    return metric.children.length > 0;
  };
  const getClass = (isExpandable: boolean) => {
    return isExpandable ? 'title-content' : 'content';
  };

  return (
    <div className={cx('body')} key={metric.item.label}>
      <Accordion.Title
        onClick={!metric.children?.entries() ? undefined : handleTitleClick}
        active={isExpanded}
        className={cx('title', {
          'no-children': metric.children.length === 0,
        })}
      >
        {!print && isExpandable(metric) && renderIcon(isExpanded)}
        <table className={cx(getClass(isExpandable(metric)))} data-testid="accordion-title">
          <tbody>{renderOverviewContentLine(metric.item, true)}</tbody>
        </table>
      </Accordion.Title>
      {metric.children.length > 0 && (
        <Accordion.Content active={isExpanded} data-testid={isExpanded ? 'expanded-content' : 'collapsed-content'}>
          <table className={cx('content')} data-testid="accordion-content">
            <tbody>{renderOverviewContent(metric.children)}</tbody>
          </table>
        </Accordion.Content>
      )}
      <Divider />
    </div>
  );
};

export default ExpandableMetric;
